import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Bell, X, CheckCheck, Package, ShoppingBag, AlertCircle, Settings, Plus,
  Trash2, Mail, Save, ArrowLeft, MessageSquare, Send, Copy, Check,
  ExternalLink, ShieldCheck, RefreshCw, Smartphone
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import usePageTitle from "@/hooks/use-page-title";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, fmtDateTime } from "@/lib/date";

export default function Notifications() {
  usePageTitle("Notifications & WhatsApp");
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [activeTab, setActiveTab] = useState("inbox"); // inbox | whatsapp
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | unread
  const [settingsOpen, setSettingsOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/notifications", { params: { limit: 200, unread_only: filter === "unread" } })
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (activeTab === "inbox") load();
  }, [filter, activeTab]);

  const markRead = (n) => api.post(`/notifications/${n.id}/read`).then(load).catch(() => {});
  const dismiss = (n) => api.delete(`/notifications/${n.id}`).then(load).catch(() => {});
  const readAll = () => api.post("/notifications/read-all").then(() => { load(); toast.success("All notifications marked as read"); }).catch(() => {});

  const iconFor = (t) => {
    if (t === "new_order") return <ShoppingBag size={16} className="text-emerald-600" />;
    if (t === "low_stock") return <Package size={16} className="text-amber-600" />;
    if (t === "unpaid") return <AlertCircle size={16} className="text-red-600" />;
    return <Bell size={16} className="text-zinc-600" />;
  };

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div className="flex flex-wrap items-end justify-between border-b border-[hsl(var(--border))] pb-6 gap-4">
        <div>
          <div className="overline mb-2">Comms &amp; Alerts</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Notifications &amp; WhatsApp</h1>
          <p className="text-sm text-zinc-500 mt-1">Manage system notifications, admin email alerts, and MSG91 WhatsApp transactional messages.</p>
        </div>
        <div className="flex items-center gap-2">
          {activeTab === "inbox" && (
            <button onClick={readAll} className="btn-swiss btn-outline" data-testid="notifs-read-all">
              <CheckCheck size={14} /> Mark all read
            </button>
          )}
          {isAdmin && activeTab === "inbox" && (
            <button onClick={() => setSettingsOpen(true)} className="btn-swiss btn-accent" data-testid="notifs-settings-btn">
              <Settings size={14} /> Alert Recipients
            </button>
          )}
        </div>
      </div>

      {/* Main Tab Switcher */}
      <div className="flex items-center gap-2 border-b border-zinc-200" data-testid="notifications-main-tabs">
        <button
          type="button"
          onClick={() => setActiveTab("inbox")}
          className={`inline-flex items-center gap-2 px-4 py-2.5 font-bold text-sm border-b-2 transition-colors ${activeTab === "inbox" ? "border-[#FF6B00] text-[#FF6B00]" : "border-transparent text-zinc-600 hover:text-zinc-900"}`}
          data-testid="tab-inbox"
        >
          <Bell size={15} /> System Inbox
        </button>
        {isAdmin && (
          <button
            type="button"
            onClick={() => setActiveTab("whatsapp")}
            className={`inline-flex items-center gap-2 px-4 py-2.5 font-bold text-sm border-b-2 transition-colors ${activeTab === "whatsapp" ? "border-[#FF6B00] text-[#FF6B00]" : "border-transparent text-zinc-600 hover:text-zinc-900"}`}
            data-testid="tab-whatsapp"
          >
            <MessageSquare size={15} /> WhatsApp Transact (MSG91)
            <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Live API</span>
          </button>
        )}
      </div>

      {activeTab === "inbox" && (
        <>
          <div className="inline-flex bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-full p-0.5">
            {[
              { v: "all", l: "All", tid: "notifs-tab-all" },
              { v: "unread", l: "Unread only", tid: "notifs-tab-unread" },
            ].map((o) => {
              const sel = filter === o.v;
              return (
                <button
                  key={o.v}
                  onClick={() => setFilter(o.v)}
                  className={`px-4 py-1.5 text-xs font-bold rounded-full transition-colors ${sel ? "bg-[#FF6B00] text-white" : "text-zinc-600 hover:text-[#FF6B00]"}`}
                  data-testid={o.tid}
                >{o.l}</button>
              );
            })}
          </div>

          <div className="card-swiss overflow-hidden">
            {loading && <div className="px-4 py-12 text-center text-zinc-500 text-sm">Loading…</div>}
            {!loading && rows.length === 0 && (
              <div className="px-4 py-16 text-center">
                <Bell size={32} className="mx-auto text-zinc-300" />
                <div className="mt-3 font-bold text-lg text-zinc-800">You're all caught up</div>
                <div className="text-xs text-zinc-500 mt-1">New alerts will appear here as they come in.</div>
              </div>
            )}
            {!loading && rows.map((n) => (
              <div
                key={n.id}
                className={`flex items-start gap-4 px-4 py-4 border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] transition-colors ${n.is_read ? "" : "bg-orange-50/50"}`}
                data-testid={`notif-item-${n.id}`}
              >
                <div className="mt-1 flex-shrink-0">{iconFor(n.type)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {!n.is_read && <span className="w-2 h-2 rounded-full bg-[#FF6B00] flex-shrink-0" />}
                    <div className="text-sm font-bold">{n.title}</div>
                  </div>
                  <div className="text-sm text-zinc-600 mt-1">{n.body}</div>
                  <div className="text-[11px] text-zinc-400 mt-1.5">{fmtDateTime(n.created_at)}</div>
                  {n.link && (
                    <Link
                      to={n.link}
                      onClick={() => !n.is_read && markRead(n)}
                      className="mt-2 inline-block text-xs font-bold text-[#FF6B00] hover:underline"
                      data-testid={`notif-open-${n.id}`}
                    >Open →</Link>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {!n.is_read && (
                    <button onClick={() => markRead(n)} className="p-1.5 text-zinc-400 hover:text-emerald-600" title="Mark read" data-testid={`notif-mark-read-${n.id}`}>
                      <CheckCheck size={14} />
                    </button>
                  )}
                  <button onClick={() => dismiss(n)} className="p-1.5 text-zinc-400 hover:text-red-500" title="Dismiss" data-testid={`notif-dismiss-${n.id}`}>
                    <X size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {activeTab === "whatsapp" && isAdmin && (
        <WhatsAppManager />
      )}

      {settingsOpen && isAdmin && <SettingsModal onClose={() => setSettingsOpen(false)} />}
    </div>
  );
}

function WhatsAppManager() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [authKeyInput, setAuthKeyInput] = useState("");
  const [copiedKey, setCopiedKey] = useState("");

  // Test send state
  const [testPhone, setTestPhone] = useState("8070970980");
  const [testKind, setTestKind] = useState("order_confirmation");
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const loadCfg = () => {
    setLoading(true);
    api.get("/admin/whatsapp/config")
      .then((r) => {
        setCfg(r.data);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  const loadLogs = () => {
    setLogsLoading(true);
    api.get("/admin/whatsapp/log")
      .then((r) => setLogs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setLogs([]))
      .finally(() => setLogsLoading(false));
  };

  useEffect(() => {
    loadCfg();
    loadLogs();
  }, []);

  const save = async (e) => {
    e?.preventDefault();
    if (!cfg) return;
    setSaving(true);
    try {
      const payload = { ...cfg };
      if (authKeyInput.trim()) {
        payload.auth_key = authKeyInput.trim();
      }
      await api.put("/admin/whatsapp/config", payload);
      toast.success("WhatsApp configuration saved");
      setAuthKeyInput("");
      loadCfg();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setSaving(false);
    }
  };

  const copyTemplate = (id, text) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(id);
    toast.success("Template text copied to clipboard");
    setTimeout(() => setCopiedKey(""), 2000);
  };

  const sendTest = async (e) => {
    e.preventDefault();
    setTestBusy(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/admin/whatsapp/test", {
        phone: testPhone,
        kind: testKind,
      });
      if (data.ok) {
        setTestResult({ ok: true, data });
        toast.success("Test WhatsApp message dispatched via MSG91!");
      } else {
        setTestResult({ ok: false, error: data.provider_error || "MSG91 rejected the send" });
        toast.error("MSG91 rejected the message — see details below");
      }
      loadLogs();
    } catch (e2) {
      const msg = formatApiError(e2);
      setTestResult({ ok: false, error: msg });
      toast.error(`Send failed: ${msg}`);
    } finally {
      setTestBusy(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-sm text-zinc-500">Loading WhatsApp configuration…</div>;
  }

  const templates = [
    {
      id: "order_confirmation",
      name: cfg?.order_template || "order_confirmation",
      category: "Utility",
      header: "None (Text only)",
      desc: "Sent to admin & customer when an order is placed.",
      body: "Your Brothers Advertising order {{1}} for {{2}} has been received. Amount: ₹{{3}}. We will update you when its status changes.",
      vars: ["{{1}} = Order No (e.g. BA-2026-000156)", "{{2}} = Customer Name", "{{3}} = Total Amount (₹)"],
    },
    {
      id: "payment_receipt",
      name: cfg?.receipt_template || "payment_receipt",
      category: "Utility",
      header: "Document (PDF)",
      desc: "Sent automatically with tokenized public GST Invoice PDF attached.",
      body: "Payment received for order {{1}}. Amount: ₹{{2}}. Your invoice is attached.",
      vars: ["{{1}} = Order No", "{{2}} = Amount Paid (₹)", "Header Document = Tokenized Invoice PDF URL"],
    },
    {
      id: "order_status",
      name: cfg?.status_template || "order_status",
      category: "Utility",
      header: "None (Text only)",
      desc: "Sent on status changes (Confirmed, Printing, Out for Delivery, Delivered).",
      body: "Update for order {{1}}: status is {{2}}. Track your order: {{3}}",
      vars: ["{{1}} = Order No", "{{2}} = Status Name", "{{3}} = Public Tracking URL"],
    },
  ];

  return (
    <div className="space-y-6" data-testid="whatsapp-manager-root">
      {/* Overview & Quick Toggle Card */}
      <div className="card-swiss p-6 bg-gradient-to-br from-white to-zinc-50 border-2 border-zinc-200">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-zinc-200 pb-5">
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-2xl ${cfg?.enabled ? "bg-emerald-500 text-white shadow-md shadow-emerald-500/20" : "bg-zinc-200 text-zinc-600"}`}>
              <MessageSquare size={24} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-black font-display tracking-tight">MSG91 WhatsApp Transactional Engine</h2>
                <span className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full ${cfg?.enabled ? "bg-emerald-100 text-emerald-800 border border-emerald-300" : "bg-zinc-200 text-zinc-700"}`}>
                  {cfg?.enabled ? "Active & Sending" : "Standby (Disabled)"}
                </span>
              </div>
              <p className="text-xs text-zinc-500 mt-0.5">
                Integrated Number: <b className="font-mono text-zinc-900">+{cfg?.integrated_number || "918070970980"}</b> · Auth Key: <span className="font-mono text-zinc-700">{cfg?.auth_key_masked || "Configured in .env"}</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2.5 cursor-pointer bg-white px-4 py-2 rounded-full border border-zinc-200 shadow-sm hover:border-[#FF6B00]">
              <input
                type="checkbox"
                checked={!!cfg?.enabled}
                onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
                className="w-4 h-4 text-[#FF6B00] rounded focus:ring-0 cursor-pointer"
                data-testid="wa-enabled-checkbox"
              />
              <span className="text-xs font-bold uppercase tracking-wider">Enable WhatsApp</span>
            </label>
            <button
              onClick={save}
              disabled={saving}
              className="btn-swiss btn-accent text-xs"
              data-testid="wa-save-btn"
            >
              <Save size={14} /> {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>

        {/* Settings grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-5 text-xs">
          <div>
            <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">WhatsApp Integrated Number</label>
            <input
              value={cfg?.integrated_number || ""}
              onChange={(e) => setCfg({ ...cfg, integrated_number: e.target.value })}
              placeholder="918070970980 (no + or spaces)"
              className="w-full border border-zinc-200 bg-white rounded-lg px-3 py-2 font-mono text-xs outline-none focus:border-[#FF6B00]"
              data-testid="wa-integrated-number-input"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Admin Alert Mobile</label>
            <input
              value={cfg?.admin_notify_phone || ""}
              onChange={(e) => setCfg({ ...cfg, admin_notify_phone: e.target.value })}
              placeholder="+918070970980"
              className="w-full border border-zinc-200 bg-white rounded-lg px-3 py-2 font-mono text-xs outline-none focus:border-[#FF6B00]"
              data-testid="wa-admin-phone-input"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Template Language Code</label>
            <input
              value={cfg?.template_language || "en"}
              onChange={(e) => setCfg({ ...cfg, template_language: e.target.value })}
              placeholder="en or en_US"
              className="w-full border border-zinc-200 bg-white rounded-lg px-3 py-2 font-mono text-xs outline-none focus:border-[#FF6B00]"
              data-testid="wa-language-input"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Update Auth Key (optional)</label>
            <input
              type="password"
              value={authKeyInput}
              onChange={(e) => setAuthKeyInput(e.target.value)}
              placeholder="Leave blank to keep existing"
              className="w-full border border-zinc-200 bg-white rounded-lg px-3 py-2 font-mono text-xs outline-none focus:border-[#FF6B00]"
              data-testid="wa-authkey-input"
            />
          </div>
        </div>
      </div>

      {/* Template Setup Hub */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-display font-black text-lg">3 Required Meta-Approved Templates</h3>
            <p className="text-xs text-zinc-500">Go to <b>MSG91 → WhatsApp → Templates → Create Template</b> and submit the exact text below:</p>
          </div>
          <a
            href="https://control.msg91.com"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-bold text-[#FF6B00] hover:underline"
          >
            Open MSG91 Dashboard <ExternalLink size={12} />
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {templates.map((t) => (
            <div key={t.id} className="card-swiss p-4 flex flex-col justify-between border-2 border-zinc-200 bg-white" data-testid={`template-card-${t.id}`}>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-xs font-black text-[#FF6B00]">{t.name}</span>
                  <span className="text-[9px] uppercase font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">{t.category}</span>
                </div>
                <div className="text-[10px] text-zinc-500 mb-2"><b>Header:</b> {t.header}</div>
                <div className="p-3 bg-zinc-50 border border-zinc-200 rounded-lg text-xs font-mono text-zinc-800 leading-relaxed mb-3">
                  {t.body}
                </div>
                <div className="space-y-1 mb-3">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Variables mapping:</div>
                  {t.vars.map((v, idx) => (
                    <div key={idx} className="text-[11px] text-zinc-600 font-mono">• {v}</div>
                  ))}
                </div>
              </div>
              <button
                type="button"
                onClick={() => copyTemplate(t.id, t.body)}
                className="btn-swiss btn-outline text-xs w-full justify-center gap-1.5 mt-2"
                data-testid={`copy-template-${t.id}`}
              >
                {copiedKey === t.id ? <><Check size={13} className="text-emerald-600" /> Copied!</> : <><Copy size={13} /> Copy Template Text</>}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Live Testing Sandbox */}
      <div className="card-swiss p-5 bg-white border-2 border-zinc-200" data-testid="wa-testing-sandbox">
        <div className="border-b border-zinc-200 pb-3 mb-4">
          <div className="overline text-[#FF6B00]">Interactive Test Sandbox</div>
          <h3 className="font-display font-black text-lg">Send Sample WhatsApp Message</h3>
          <p className="text-xs text-zinc-500 mt-0.5">Test any template end-to-end to verify MSG91 and Meta routing to your phone.</p>
        </div>

        <form onSubmit={sendTest} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Select Template</label>
            <select
              value={testKind}
              onChange={(e) => setTestKind(e.target.value)}
              className="w-full border border-zinc-200 bg-white rounded-lg px-3 py-2 text-xs font-mono outline-none focus:border-[#FF6B00]"
              data-testid="wa-test-kind"
            >
              <option value="order_confirmation">order_confirmation (Order Alert)</option>
              <option value="payment_receipt">payment_receipt (Invoice PDF Attached)</option>
              <option value="order_status">order_status (Status Update)</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Test Mobile Number</label>
            <input
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder="918070970980"
              required
              className="w-full border border-zinc-200 bg-white rounded-lg px-3 py-2 text-xs font-mono outline-none focus:border-[#FF6B00]"
              data-testid="wa-test-phone"
            />
          </div>
          <div className="md:col-span-2 flex items-center gap-2">
            <button
              type="submit"
              disabled={testBusy}
              className="btn-swiss btn-accent text-xs flex-1 justify-center py-2.5"
              data-testid="wa-test-send-btn"
            >
              <Send size={13} /> {testBusy ? "Sending via MSG91…" : "Send Test Message →"}
            </button>
            <button
              type="button"
              onClick={loadLogs}
              className="btn-swiss btn-outline text-xs px-3 py-2.5"
              title="Refresh logs"
              data-testid="wa-refresh-logs-btn"
            >
              <RefreshCw size={13} className={logsLoading ? "animate-spin" : ""} />
            </button>
          </div>
        </form>

        {testResult && (
          <div className={`mt-4 p-3 rounded-lg text-xs font-mono border ${testResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-800"}`} data-testid="wa-test-result">
            <b>{testResult.ok ? "✓ Success Response:" : "✗ Error Response:"}</b>
            <pre className="mt-1 whitespace-pre-wrap text-[11px] overflow-x-auto">{JSON.stringify(testResult.data || testResult.error, null, 2)}</pre>
          </div>
        )}
      </div>

      {/* Recent Activity Log */}
      <div className="card-swiss overflow-hidden">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <h3 className="font-display font-black text-base">Recent WhatsApp Dispatch Logs</h3>
            <p className="text-xs text-zinc-500">Live delivery reports received from MSG91 webhook.</p>
          </div>
          <span className="text-xs text-zinc-500">{logs.length} logged sends</span>
        </div>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-[10px] uppercase font-bold text-zinc-500">
              <th className="px-4 py-2.5">Time</th>
              <th className="px-4 py-2.5">Order</th>
              <th className="px-4 py-2.5">Recipient</th>
              <th className="px-4 py-2.5">Template</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5">Request ID</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((l, idx) => (
              <tr key={idx} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`wa-log-row-${idx}`}>
                <td className="px-4 py-2.5 font-mono text-zinc-500">{fmtDateTime(l.created_at)}</td>
                <td className="px-4 py-2.5 font-bold font-mono">{l.order_id || "—"}</td>
                <td className="px-4 py-2.5 font-mono">+{l.recipient}</td>
                <td className="px-4 py-2.5 font-mono text-[#FF6B00]">{l.template}</td>
                <td className="px-4 py-2.5">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider ${
                    l.status === "delivered" || l.status === "read" ? "bg-emerald-100 text-emerald-800" :
                    l.status === "queued" || l.status === "sent" ? "bg-blue-100 text-blue-800" :
                    l.status === "submit_failed" || l.status === "failed" ? "bg-red-100 text-red-800" : "bg-zinc-100 text-zinc-700"
                  }`}>
                    {l.status}
                  </span>
                </td>
                <td className="px-4 py-2.5 font-mono text-zinc-400 text-[10px]">{l.request_id || "—"}</td>
              </tr>
            ))}
            {logs.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-zinc-400">
                  No WhatsApp messages logged yet. Use the Test Sandbox above to send your first message.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SettingsModal({ onClose }) {
  const [enabled, setEnabled] = useState(true);
  const [recips, setRecips] = useState([{ name: "", email: "" }]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/notification-settings").then((r) => {
      setEnabled(!!r.data.enabled);
      const list = (r.data.new_order_recipients || []).map((x) => ({ name: x.name || "", email: x.email || "" }));
      setRecips(list.length ? list : [{ name: "", email: "" }]);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const updateRow = (i, patch) => setRecips((prev) => prev.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const removeRow = (i) => setRecips((prev) => prev.filter((_, idx) => idx !== i));
  const addRow = () => setRecips((prev) => [...prev, { name: "", email: "" }]);

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = recips.map((r) => ({ name: (r.name || "").trim(), email: (r.email || "").trim().toLowerCase() }))
                            .filter((r) => r.email && /.+@.+\..+/.test(r.email));
      await api.put("/admin/notification-settings", { enabled, new_order_recipients: cleaned });
      toast.success("Notification settings saved");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="notif-settings-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
          <div>
            <div className="overline text-[#FF6B00]">Configuration</div>
            <div className="font-display text-xl font-black tracking-tighter">Alert Recipients</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-full" data-testid="notif-settings-close"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-5">
          {loading ? (
            <div className="text-center text-sm text-zinc-500 py-4">Loading…</div>
          ) : (
            <>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="notif-enabled-toggle" />
                <div>
                  <div className="font-bold text-sm">Email alerts on new orders</div>
                  <div className="text-xs text-zinc-500">Send an email to the addresses below every time a new web or manual order is created.</div>
                </div>
              </label>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-wider font-bold text-zinc-500 inline-flex items-center gap-1"><Mail size={11} /> Recipients</div>
                  <button onClick={addRow} className="text-xs font-bold text-[#FF6B00] hover:underline inline-flex items-center gap-1" data-testid="notif-add-recipient">
                    <Plus size={12} /> Add
                  </button>
                </div>
                <div className="space-y-2">
                  {recips.map((r, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`notif-recip-row-${i}`}>
                      <input
                        value={r.name}
                        onChange={(e) => updateRow(i, { name: e.target.value })}
                        placeholder="Name (optional)"
                        className="flex-1 border-2 border-zinc-200 focus:border-[#FF6B00] rounded-xl px-3 py-2 text-sm outline-none"
                        data-testid={`notif-recip-name-${i}`}
                      />
                      <input
                        value={r.email}
                        onChange={(e) => updateRow(i, { email: e.target.value })}
                        placeholder="staff@brothersdigital.in"
                        className="flex-1 border-2 border-zinc-200 focus:border-[#FF6B00] rounded-xl px-3 py-2 text-sm outline-none font-mono"
                        data-testid={`notif-recip-email-${i}`}
                      />
                      <button onClick={() => removeRow(i)} className="p-1.5 text-zinc-400 hover:text-red-500" data-testid={`notif-recip-remove-${i}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="text-[11px] text-zinc-500 bg-amber-50 border border-amber-200 rounded-xl p-3">
                <b>💡 Tip:</b> Emails are sent from <b>alerts@brothersdigital.in</b>. Reply-to lands in the existing support inbox.
              </div>
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t border-zinc-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="btn-swiss btn-outline text-xs" data-testid="notif-settings-cancel">Cancel</button>
          <button onClick={save} disabled={saving || loading} className="btn-swiss btn-accent text-xs disabled:opacity-50" data-testid="notif-settings-save">
            <Save size={12} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
